import {useSave} from "@/contexts/SaveContext";
import {useState, useEffect, useRef} from "react";
import {PageLeaveModal} from "./PageLeaveModal";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";

export const PageLeaveGuard = () => {
  const {hasUnsavedChanges} = useSave();
  const [showModal, setShowModal] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [confirmedLeave, setConfirmedLeave] = useState(false);
  const isNavigating = useRef(false);
  const currentUrl = useRef(window.location.href);

  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (hasUnsavedChanges && !FlowBuilderSettings.demoMode) {
      e.preventDefault();
      e.returnValue = "";
      return "";
    }
  };

  const handleClick = (e: MouseEvent) => {
    if (isNavigating.current || FlowBuilderSettings.demoMode) return;

    const target = (e.target as HTMLElement).closest("a, div[data-navigate]");
    if (!target) return;

    const href =
      target?.getAttribute("href") || target?.getAttribute("data-navigate");
    if (!href) return;

    // Handle both internal navigation and external links
    if (hasUnsavedChanges) {
      e.preventDefault();
      e.stopImmediatePropagation();
      window.removeEventListener("beforeunload", handleBeforeUnload);
      isNavigating.current = true;
      setPendingUrl(href);
      setShowModal(true);
    }
  };

  const interceptHistoryChanges = () => {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      if (hasUnsavedChanges && !isNavigating.current) {
        const newUrl =
          typeof args[2] === "string" ? args[2] : window.location.href;
        if (newUrl !== currentUrl.current) {
          isNavigating.current = true;
          setPendingUrl(newUrl);
          setShowModal(true);
          return;
        }
      }
      currentUrl.current = window.location.href;
      return originalPushState.apply(this, args);
    };

    history.replaceState = function (...args) {
      if (hasUnsavedChanges && !isNavigating.current) {
        const newUrl =
          typeof args[2] === "string" ? args[2] : window.location.href;
        if (newUrl !== currentUrl.current) {
          isNavigating.current = true;
          setPendingUrl(newUrl);
          setShowModal(true);
          return;
        }
      }
      currentUrl.current = window.location.href;
      return originalReplaceState.apply(this, args);
    };

    const handlePopState = () => {
      if (hasUnsavedChanges && !isNavigating.current) {
        const newUrl = window.location.href;
        if (newUrl !== currentUrl.current) {
          isNavigating.current = true;
          setPendingUrl(newUrl);
          setShowModal(true);
          history.pushState(null, "", currentUrl.current);
        }
      } else {
        currentUrl.current = window.location.href;
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handlePopState);
    };
  };

  useEffect(() => {
    if (FlowBuilderSettings.demoMode && !hasUnsavedChanges) return;

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleClick, true);
    const cleanup = interceptHistoryChanges();

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleClick, true);
      cleanup();
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (FlowBuilderSettings.demoMode) return;

    if (!showModal) {
      isNavigating.current = false;
      if (confirmedLeave && pendingUrl) {
        window.location.href = pendingUrl;
      }
    }
  }, [showModal, confirmedLeave, pendingUrl]);

  const handleLeave = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setConfirmedLeave(true);
    setShowModal(false);
  };

  const handleCancel = () => {
    setPendingUrl(null);
    setConfirmedLeave(false);
    setShowModal(false);
    isNavigating.current = false;
    window.addEventListener("beforeunload", handleBeforeUnload);
  };

  return (
    <PageLeaveModal
      onLeave={handleLeave}
      onCancel={handleCancel}
      showModal={showModal}
      setShowModal={setShowModal}
    />
  );
};
